import React from 'react';
import { ExternalLink, Link } from '../router';
import MonthlyWorkshop from '../components/MonthlyWorkshop';
import { BOOKING_URL } from '../links';
import { useOpenNewsletter } from '../newsletter';

const coachingCards: { id?: string; title: string; description: string }[] = [
  {
    id: 'blood-work',
    title: 'Blood Test Analysis & Nutrition',
    description: 'A clear read of your blood work, and a nutrition and herbal plan built on what it shows.',
  },
  {
    title: 'Applied Kinesiology & Muscle Testing',
    description: 'A full-body read, finding where your system is under load and what is driving it.',
  },
  { title: 'CranioSacral Therapy', description: 'Gentle hands-on work to release tension held deep in the nervous system.' },
  {
    title: 'Herbal Medicine Consultation',
    description: 'A personalised herbal protocol to support your energy, sleep, and recovery.',
  },
  { title: 'NLP Coaching', description: 'Reframe the mental patterns that are quietly limiting your performance.' },
  { title: 'MRI Interpretation & Second Opinion', description: 'A clinical read of your scan with clear, plain-language findings.' },
];

const WorkWithMe: React.FC = () => {
  const openNewsletter = useOpenNewsletter();

  return (
    <>
      <section className="section page-hero">
        <div className="container container--narrow center">
          <h1>Work With Me</h1>
          <nav className="jump-links" aria-label="Offers on this page">
            {[
              ['#session', 'Complimentary Consultation'],
              ['#signature', 'Reclaim Your Cognitive Edge'],
              ['#focus-reboot', 'The Focus Reboot'],
              ['#nlp', 'NLP Leadership Session'],
              ['#blood-work', 'Blood Test Analysis & Nutrition'],
              ['#premium', 'Premium Coaching'],
              ['#retreat', 'Neuro Recharge Retreat'],
              ['#walk', 'Walk to Possibilities'],
              ['#companies', 'For Companies'],
              ['#monthly-workshop', 'Monthly Workshop'],
            ].map(([hash, label]) => (
              <Link key={hash} to={`/work-with-me${hash}`}>
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <section className="section" id="session">
        <div className="container container--narrow">
          <h2>Complimentary Performance Strategy Session</h2>
          <p>
            A private 1:1 call, <strong>at no cost</strong>. We map exactly what is blocking your focus, energy, or performance,
            and what it would take to fix it.
          </p>
          <p>
            <strong>This is not a sales call.</strong> I only suggest working together if I genuinely believe I can help you. If I don't, I
            will tell you straight.
          </p>
          <ExternalLink href={BOOKING_URL} className="btn btn-primary">
            Book Your Complimentary Session
          </ExternalLink>
        </div>
      </section>

      <section className="section section--alt" id="signature">
        <div className="container container--narrow">
          <h2>Reclaim Your Cognitive Edge: Signature 1:1 Program</h2>
          <p>
            My flagship program for technical professionals who want their edge back and a system that keeps it. Built on the{' '}
            <strong>Square of Health</strong> and the <strong>EDGE process</strong>. <strong>Results within 6 months.</strong>
          </p>
          <p>
            Also available as the <strong>5-day Focus Reboot</strong> intensive at{' '}
            <ExternalLink href="https://focus-ntap.hannakinez.com/">focus-ntap.hannakinez.com</ExternalLink>. One focused
            intervention, adjusted to you, for a fast concentrated start.
          </p>
          <ExternalLink href={BOOKING_URL} className="btn btn-primary">
            Book a Call About the Program
          </ExternalLink>
        </div>
      </section>

      <section className="section" id="focus-reboot">
        <div className="container container--narrow">
          <h2>The Focus Reboot: 5-Day 1:1 Intensive</h2>
          <p>
            <strong>Five days, 20 minutes a day, 1:1.</strong> I find the root cause of your lost focus across the four sides of
            your Square of Health and apply one real intervention, adjusted every day until it lands.
          </p>
          <p>
            <em>Free, with a $50 refundable deposit. Limited places each month.</em>
          </p>
          <ExternalLink href="https://focus-ntap.hannakinez.com/#apply" className="btn btn-primary">
            Apply for the Focus Reboot
          </ExternalLink>
        </div>
      </section>

      <section className="section photo-section" id="retreat" style={{ backgroundImage: 'url(/panoramic1.jpg)' }}>
        <div className="container container--narrow">
          <h2>The Neuro Recharge Retreat</h2>
          <p>
            A <strong>5-day in-person reset</strong> for technical leaders running on empty: burnout, brain fog, tech posture,
            decision fatigue.
          </p>
          <p>
            <em>5-star venue, all-inclusive, small group, 1:1 plus group work.</em>
          </p>
          <p>
            <strong>Next dates: coming soon.</strong> Get on the KinezioLetter to hear first and receive a special subscriber
            offer.
          </p>
          <button className="btn btn-primary" onClick={openNewsletter}>
            Join the KinezioLetter for Dates
          </button>
        </div>
      </section>

      <section className="section section--alt" id="walk">
        <div className="container container--narrow">
          <h2>Walk to Possibilities</h2>
          <p>
            A <strong>30-day, self-paced body reset</strong>. <strong>15–35 minutes a day. No gym, no diet changes.</strong>
          </p>
          <p>
            Video techniques, private Telegram support, and a community. My own method: first results in about two weeks, and the
            full result within a month.
          </p>
          <ExternalLink href="https://www.walk2possibilities.com/" className="btn btn-primary">
            Start Walking
          </ExternalLink>
        </div>
      </section>

      <section className="section" id="premium">
        <div className="container center">
          <h2>Premium Coaching Sessions</h2>
          <div className="card-grid">
            {coachingCards.map((card) => (
              <div key={card.title} id={card.id} className="card">
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            ))}
          </div>
          <Link to="/contact" className="btn btn-primary">
            Contact Me
          </Link>
        </div>
      </section>

      <section className="section section--alt" id="nlp">
        <div className="container container--narrow">
          <h2>NLP Leadership Session</h2>
          <p>
            <strong>One focused NLP coaching session</strong> to break a problem cycle you keep running into: difficult team
            dynamics, communication barriers, or feeling overwhelmed as a leader. You leave with a fresh way of seeing it, and a
            plan you can follow through on.
          </p>
          <p>
            <em>Start with a complimentary 20-minute discovery call.</em>
          </p>
          <ExternalLink href="https://nlp.hannakinez.com/" className="btn btn-primary">
            Explore the NLP Session
          </ExternalLink>
        </div>
      </section>

      <section className="section" id="joint">
        <div className="container container--narrow">
          <h2>Joint Projects &amp; Collaborations</h2>
          <ul className="list">
            <li>
              <ExternalLink href="https://wiredtowin.hannakinez.com/">
                <strong>Wired to Win</strong>
              </ExternalLink>{' '}
              — a 2-day speaking and presence workshop co-led with Chris Hanlon, TEDx speaker coach.
            </li>
            <li>Workshop with Jordan Hirsch, Public Speaking Success Coach.</li>
          </ul>
        </div>
      </section>

      <section className="section section--alt" id="companies">
        <div className="container container--narrow">
          <h2>For Companies &amp; Coworking Spaces</h2>
          <p>
            Practical workshops for <strong>mental health days, wellness days and team offsites</strong>: focus, energy,
            nutrition, posture and burnout prevention for developers and technical teams. Coworking spaces can book the same
            workshops for their members.
          </p>
          <Link to="/office" className="btn btn-primary">
            See the Offer for Companies
          </Link>
        </div>
      </section>

      <MonthlyWorkshop />
    </>
  );
};

export default WorkWithMe;
